// src/components/AgendaBookFinanceJson.js

import React, { useEffect, useState } from "react";
import { getUser } from "../utils/auth";
import { apiFetch } from "../utils/wkCommon";
import { hasUmatPermissionAsync } from "../utils/permissionsAsync";
import PERMISSIONS from "../utils/wkCommon";
import { isZh } from "../utils/language";

/* FORMAT DATA */
/*
[
  {
    "participantSummary": {

      "pesertaMale": {
        "count": 12,
        "donation": 360000
      },

      "pesertaFemale": {
        "count": 18,
        "donation": 540000
      },

      "petugasMale": {
        "count": 5,
        "donation": 150000
      },

      "petugasFemale": {
        "count": 7,
        "donation": 210000
      }

    },

    "incomes": [
      {
        "id": "f_1748339200",
        "Remarks": "",
        "created": "2026-05-24T08:28:39.000Z",
        "updated": "2026-05-24T08:28:39.000Z",
        "recordOwner": "admin",
        "recordEditor": "admin",

        "fullDonation": false,

        "originalAmount": null,
        "recordedAmount": 360000,

        "TransactionDate": "2026-05-24",

        "transactionName": "乾道班員助班費",
        "transactionType": "Income"
      },

      {
        "id": "f_1748339201",
        "Remarks": "",
        "created": "2026-05-24T08:30:00.000Z",
        "updated": "2026-05-24T08:30:00.000Z",
        "recordOwner": "admin",
        "recordEditor": "admin",

        "fullDonation": false,

        "originalAmount": null,
        "recordedAmount": 900000,

        "TransactionDate": "2026-05-24",

        "transactionName": "班員助班費",
        "transactionType": "Income"
      }
    ],

    "expenses": [
      {
        "id": "f_1748339300",
        "Remarks": "Snack peserta",

        "created": "2026-05-24T09:00:00.000Z",
        "updated": "2026-05-24T09:00:00.000Z",

        "recordOwner": "admin",
        "recordEditor": "admin",

        "fullDonation": false,

        "originalAmount": 250000,
        "recordedAmount": 250000,

        "TransactionDate": "2026-05-24",

        "transactionName": "Konsumsi",
        "transactionType": "Expense"
      }
    ]
  }
]
*/

export default function AgendaBookFinanceJson({
  agendaId,
  agendaBookId,
  agendaFinances = [],
  onFinanceUpdated
}) {

  const user = getUser();

  const todayStr = new Date().toISOString().slice(0,10);

  const emptyFinanceRow = {
    id: null,
    TransactionDate: todayStr,
    transactionType: "Income",
    transactionName: "",
    recordedAmount: "",
    originalAmount: "",
    fullDonation: false,
    Remarks: ""
  };

  const emptySummary = {
    pesertaMale: { count: 0, donation: 0 },
    pesertaFemale: { count: 0, donation: 0 },
    petugasMale: { count: 0, donation: 0 },
    petugasFemale: { count: 0, donation: 0 }
  };

  const initialFinance =
    agendaFinances?.length
      ? agendaFinances
      : [{
          participantSummary: emptySummary,
          incomes: [],
          expenses: []
        }];

  const [finance, setFinance] = useState(initialFinance);

  const [canRead, setCanRead] = useState(false);
  const [canWrite, setCanWrite] = useState(false);
  const [permsReady, setPermsReady] = useState(false);

  const [dragIndex, setDragIndex] = useState(null);
  const [dragType, setDragType] = useState(null);

  const [agendaDate, setAgendaDate] = useState(null);

  const [editingIndex, setEditingIndex] = useState(null);
  const [editingType, setEditingType] = useState(null);
  const [draft, setDraft] = useState(emptyFinanceRow);
  const [isAdding, setIsAdding] = useState(false);
	const [isCalculating, setIsCalculating] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!agendaId) return;

    async function loadAgendaInfo() {
      try {

        const resp = await apiFetch(`/api/agendas/${agendaId}`);
        const a = resp?.data || resp;

        if (a?.agendaDate) {
          setAgendaDate(a.agendaDate);
        }

      } catch (e) {
        console.warn("loadAgendaDate failed", e);
      }
    }

    loadAgendaInfo();

  }, [agendaId]);

  /* ================= PERMISSION ================= */

  useEffect(() => {

    let mounted = true;

    async function loadPerms() {

      const email = user?.email || user?.LoginEmail;

      const r = await hasUmatPermissionAsync(
        email,
        PERMISSIONS.AGENDA_EXPENSE_READ
      );

      const w = await hasUmatPermissionAsync(
        email,
        PERMISSIONS.AGENDA_EXPENSE_WRITE
      );

      if (mounted) {
        setCanRead(Boolean(r));
        setCanWrite(Boolean(w));
        setPermsReady(true);
      }
    }

    loadPerms();

    return () => (mounted = false);

  }, [user]);

  if (!permsReady) return <div>Loading...</div>;
  if (!canRead) return <div>Tidak ada akses / 无权限</div>;

  const data = finance[0];

  /* ================= HELPERS ================= */

  function subtotal(list) {
    return list.reduce(
      (sum, i) => sum + Number(i.recordedAmount || 0),
      0
    );
  }

  const incomeTotal = subtotal(data.incomes || []);
  const expenseTotal = subtotal(data.expenses || []);
  const balance = incomeTotal - expenseTotal;

  function isMaleGender(g) {
    return (g || "").includes("乾");
  }

  function isFemaleGender(g) {
    return (g || "").includes("坤");
  }

  function getSummary() {
    return data.participantSummary || emptySummary;
  }

  function summaryCount(key) {
    return Number(getSummary()?.[key]?.count || 0);
  }

  function summaryDonation(key) {
    return Number(getSummary()?.[key]?.donation || 0);
  }

  function totalPesertaCount() {
    return (
      summaryCount("pesertaMale") +
      summaryCount("pesertaFemale")
    );
  }

  function totalPesertaDonation() {
    return (
      summaryDonation("pesertaMale") +
      summaryDonation("pesertaFemale")
    );
  }

  function totalPetugasCount() {
    return (
      summaryCount("petugasMale") +
      summaryCount("petugasFemale")
    );
  }

  function totalPetugasDonation() {
    return (
      summaryDonation("petugasMale") +
      summaryDonation("petugasFemale")
    );
  }

  /* ================= PARTICIPANT SUMMARY ================= */

  async function handleCalculateParticipants() {
		if (isCalculating) return;

		setIsCalculating(true);

    try {

			let page = 1;
			let allRows = [];
			let hasMore = true;

			while (hasMore) {

				const url =
					`/api/event-participants` +
					`?filters%5Bevent%5D%5Bagenda%5D%5BdocumentId%5D%5B$eq%5D=${encodeURIComponent(agendaId)}` +
					`&populate%5B0%5D=umat` +
					`&populate%5B1%5D=external_person` +
					`&populate%5B2%5D=event` +
					`&pagination%5Bpage%5D=${page}` +
					`&pagination%5BpageSize%5D=200`;

				const resp = await apiFetch(url);

				const rows = resp?.data || [];

				allRows = [...allRows, ...rows];

				const meta = resp?.meta?.pagination;

				hasMore = page < (meta?.pageCount || 1);

				page++;
			}

      const result = {
        pesertaMale: { count: 0, donation: 0 },
        pesertaFemale: { count: 0, donation: 0 },
        petugasMale: { count: 0, donation: 0 },
        petugasFemale: { count: 0, donation: 0 }
      };

      allRows.forEach(r => {

        let gender = r.gender || "";

        if (r.personType === "Internal") {
          gender = r.umat?.gender || gender;
        }

        if (r.personType === "External") {
          gender = r.external_person?.gender || gender;
        }

        const donation = Number(r.donationAmount || 0);

        const isPeserta = r.peserta === true;

        if (isPeserta) {

          if (isMaleGender(gender)) {
            result.pesertaMale.count += 1;
            result.pesertaMale.donation += donation;
          }

          if (isFemaleGender(gender)) {
            result.pesertaFemale.count += 1;
            result.pesertaFemale.donation += donation;
          }

        } else {

          if (isMaleGender(gender)) {
            result.petugasMale.count += 1;
            result.petugasMale.donation += donation;
          }

          if (isFemaleGender(gender)) {
            result.petugasFemale.count += 1;
            result.petugasFemale.donation += donation;
          }

        }

      });

      const updated = [...finance];

      updated[0].participantSummary = result;

      setFinance(updated);

      alert(
        isZh()
          ? "統計成功"
          : "Rekap participant berhasil dihitung."
      );

    } catch (e) {
      console.error(e);
      alert("Gagal menghitung participant.");
    }
		finally {
			setIsCalculating(false);
		}
  }

	function editSummaryCount(key, label) {

		const current =
			data.participantSummary?.[key]?.count || 0;

		const val = window.prompt(
			`${isZh() ? "修改人數" : "Ubah jumlah"}: ${label}`,
			current
		);

		if (val === null) return;

		const num = Number(val);

		if (Number.isNaN(num)) return;

		const updated = [...finance];

		updated[0].participantSummary[key].count = num;

		setFinance(updated);
	}

  /* ================= INSERT TRANSACTION ================= */

  function insertDonationTransaction({
    transactionName,
    amount
  }) {

    const exists = (data.incomes || []).some(
      i =>
        i.transactionName === transactionName &&
        i.TransactionDate === agendaDate
    );

    if (exists) {
      alert(
        isZh()
          ? "交易已存在"
          : "Transaksi sudah ada."
      );
      return;
    }

    const now = new Date().toISOString();

    const payload = {
      id: `f_${Date.now()}_${Math.random()}`,
      TransactionDate: agendaDate || todayStr,
      transactionType: "Income",
      transactionName,
      recordedAmount: Number(amount || 0),
      originalAmount: null,
      fullDonation: false,
      Remarks: "",
      recordOwner: user?.username,
      recordEditor: user?.username,
      created: now,
      updated: now
    };

    const updated = [...finance];

    updated[0].incomes = [
      ...(updated[0].incomes || []),
      payload
    ];

    setFinance(updated);
  }

  /* ================= ADD / EDIT ================= */

  function handleAdd() {

    if (!canWrite) return;

    setIsAdding(true);
    setEditingIndex(null);
    setEditingType(null);

    setDraft({
      ...emptyFinanceRow,
      TransactionDate: agendaDate || todayStr
    });
  }

  function handleEdit(idx, type) {

    if (!canWrite) return;

    setIsAdding(false);
    setEditingType(type);
    setEditingIndex(idx);

    const row = data[type][idx];

    setDraft({
      ...row,
      TransactionDate: row.TransactionDate || todayStr,
      recordedAmount: row.recordedAmount ?? "",
      originalAmount: row.originalAmount ?? "",
      fullDonation: !!row.fullDonation
    });
  }

  function handleCancel() {

    setIsAdding(false);
    setEditingIndex(null);
    setDraft(emptyFinanceRow);
  }

  function handleSaveRow() {

    if (!canWrite) return;

    if (
      !draft.TransactionDate ||
      !draft.transactionType ||
      !draft.transactionName ||
      draft.recordedAmount === ""
    ) {
      alert(
        isZh()
          ? "請填寫必填欄位"
          : "Field wajib belum lengkap."
      );
      return;
    }

    const targetType =
      editingIndex !== null
        ? editingType
        : draft.transactionType === "Income"
          ? "incomes"
          : "expenses";

    const isExpense =
      draft.transactionType === "Expense";

    let finalOriginal = null;
    let finalFullDonation = false;
    let finalRecorded = Number(draft.recordedAmount);

    if (isExpense) {

      finalOriginal = draft.originalAmount
        ? Number(draft.originalAmount)
        : null;

      finalFullDonation = !!draft.fullDonation;

      if (finalFullDonation) {
        finalRecorded = 0;
      }
    }

    const now = new Date().toISOString();

    const payload = {
      ...draft,
      recordedAmount: finalRecorded,
      originalAmount: finalOriginal,
      fullDonation: finalFullDonation,
      recordOwner:
        editingIndex === null
          ? user?.username
          : data[targetType][editingIndex]?.recordOwner,
      recordEditor: user?.username,
      created:
        editingIndex === null
          ? now
          : data[targetType][editingIndex]?.created,
      updated: now
    };

    const updated = [...finance];

    const list = Array.isArray(updated[0][targetType])
      ? [...updated[0][targetType]]
      : [];

    if (editingIndex === null) {

      payload.id = `f_${Date.now()}`;

      list.push(payload);

    } else {

      list[editingIndex] = payload;
    }

    updated[0][targetType] = list;

    setFinance(updated);

    handleCancel();
  }

  /* ================= DRAG DROP ================= */

  function handleDragStart(index, type) {
    setDragIndex(index);
    setDragType(type);
  }

  function handleDrop(index, type) {

    if (dragType !== type) return;

    const updated = [...finance];

    const list = [...updated[0][type]];

    const [moved] = list.splice(dragIndex, 1);

    list.splice(index, 0, moved);

    updated[0][type] = list;

    setFinance(updated);

    setDragIndex(null);
    setDragType(null);
  }

  /* ================= DELETE ================= */

  function handleDelete(index, type) {

    if (!canWrite) return;

    const ok = window.confirm(
      isZh()
        ? "確定刪除此項？"
        : "Yakin ingin menghapus item ini?"
    );

    if (!ok) return;

    const updated = [...finance];

    updated[0][type].splice(index, 1);

    setFinance(updated);
  }

  /* ================= SUBMIT ================= */

  async function handleSubmit() {
		if (isSubmitting) return;
		
    if (!canWrite) return;

    if (!agendaBookId) {
      alert("AgendaBook belum siap.");
      return;
    }

		setIsSubmitting(true);

    try {

      await apiFetch(`/api/agenda-books/${agendaBookId}`, {
        method: "PUT",
        body: {
          data: {
            agendaFinances: finance
          }
        }
      });

      const fresh = await apiFetch(
        `/api/agenda-books/${agendaBookId}?populate=*`
      );

      const updated =
        fresh?.data?.agendaFinances ||
        fresh?.agendaFinances ||
        [];

      setFinance(updated);

      if (onFinanceUpdated) {
        onFinanceUpdated(updated);
      }

      alert(
        isZh()
          ? "收支表保存成功"
          : "Catatan donasi & biaya pengeluaran sudah berhasil disimpan."
      );

    } catch (e) {

      console.error(e);

      alert(
        isZh()
          ? "收支表保存失敗"
          : "Gagal menyimpan."
      );
    }
		finally {
			setIsSubmitting(false);
		}
	}

  /* ================= TABLE ================= */

  function renderFinanceTable({
    title,
    type,
    amountLabel,
    showOriginal
  }) {

    const list = data[type] || [];

    return (
      <>
        <h3 style={{ marginTop: "2rem" }}>
          {title}
        </h3>

        <table className="r-table">

          <thead>
            <tr>
              <th className="r-th-drag"></th>
              <th>{isZh() ? "項目" : "Transaksi"}</th>
              <th style={{ width: "10rem" }}>
                {amountLabel}
              </th>
              {canWrite && (
                <th className="r-th-action"></th>
              )}
            </tr>
          </thead>

          <tbody>

            {list.map((r, idx) => (

              <tr
                key={r.id}
                className="r-row"
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => handleDrop(idx, type)}
              >

                <td className="r-td-drag">

                  {canWrite && (
                    <span
                      className="drag-handle"
                      draggable
                      onDragStart={() =>
                        handleDragStart(idx, type)
                      }
                    >
                      ||
                    </span>
                  )}

                </td>

                <td>

                  <div>

                    <span
                      className="meta-pill"
                      style={{
                        background:
                          r.transactionType === "Income"
                            ? "#e6f4ea"
                            : "#fee2e2",
                        color:
                          r.transactionType === "Income"
                            ? "#166534"
                            : "#991b1b"
                      }}
                    >
                      {r.transactionType === "Income"
                        ? (isZh() ? "收入" : "Donasi")
                        : (isZh() ? "支出" : "Biaya")}
                    </span>

                    <span
                      style={{
                        fontSize: "1.05rem",
                        fontWeight: 700
                      }}
                    >
                      {r.transactionName}
                    </span>

                  </div>

                  <div
                    style={{
                      fontSize: "0.9rem",
                      color: "#64748b",
                      marginTop: "0.35rem"
                    }}
                  >

                    {r.TransactionDate !== agendaDate &&
                      r.TransactionDate}

                    {r.TransactionDate !== agendaDate &&
                      r.Remarks && (
                        <span
                          style={{
                            margin: "0 0.4rem",
                            color: "#cbd5e1"
                          }}
                        >
                          |
                        </span>
                      )}

                    {r.Remarks}

                  </div>

                  {showOriginal && (
                    <div
                      style={{
                        fontSize: "0.9rem",
                        color: "#475569",
                        marginTop: "0.25rem"
                      }}
                    >
                      {isZh()
                        ? "原始金額: "
                        : "Jumlah asli: "}
                      Rp{" "}
                      {Number(
                        r.originalAmount || 0
                      ).toLocaleString()}

                      {r.fullDonation === true && (
                        <span
                          style={{
                            marginLeft: "0.4rem",
                            fontWeight: 600
                          }}
                        >
                          ({isZh()
                            ? "了愿"
                            : "Donasi Penuh"})
                        </span>
                      )}
                    </div>
                  )}

                </td>

                <td style={{ fontWeight: 600 }}>
                  Rp{" "}
                  {Number(
                    r.recordedAmount
                  ).toLocaleString()}
                </td>

                {canWrite && (

                  <td className="r-td-action">

                    <div className="r-action-group">

                      <button
                        className="btn btn-edit btn-icon"
                        onClick={() =>
                          handleEdit(idx, type)
                        }
                      >
                        ✏️
                      </button>

                      <button
                        className="btn btn-danger btn-icon"
                        onClick={() =>
                          handleDelete(idx, type)
                        }
                      >
                        ✕
                      </button>

                    </div>

                  </td>

                )}

              </tr>

            ))}

          </tbody>

        </table>

        <div
          style={{
            textAlign: "right",
            marginTop: "0.5rem",
            fontWeight: 700
          }}
        >
          {isZh() ? "小計" : "Subtotal"}:
          {" "}
          Rp {subtotal(list).toLocaleString()}
        </div>
      </>
    );
  }

  /* ================= RENDER ================= */

  return (
    <>
      <div className="rundown-list">

        {/* ================= PARTICIPANT SUMMARY ================= */}

        <div className="abf-summary-card">

          <div className="abf-summary-header">

            <h3 style={{ margin: 0 }}>
              {isZh()
                ? "班員與辦事統計"
                : "Rekap Partisipan & Donasi"}
            </h3>

            {canWrite && (
              <button
                className="btn btn-secondary"
							  disabled={isCalculating}
                onClick={handleCalculateParticipants}
              >
								{isCalculating
									? (isZh() ? "計算中..." : "Loading...")
									: (isZh()
											? "重新計算"
											: "Hitung Jumlah Partisipan & Donasi")}
							</button>
            )}

          </div>

          {/* PESERTA */}

          <div className="abf-summary-group-title">
            {isZh() ? "班員" : "Peserta"}
          </div>

          <div className="abf-summary-row">
						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Peserta Pria
							</div>
							<div className="abf-summary-count">
								{summaryCount("pesertaMale")} orang
							</div>
							<div className="abf-summary-donation">
								Rp {summaryDonation("pesertaMale").toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button
									className="abf-summary-icon-btn abf-summary-edit-btn"
									onClick={() => editSummaryCount("pesertaMale", "peserta pria")}
								>
									<img src="/icons/editnumber.svg" alt="edit jumlah peserta pria" className="abf-summary-icon" />
								</button>
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "乾道班員助班費", amount: summaryDonation("pesertaMale")})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

          <div className="abf-summary-row">
						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Peserta Wanita
							</div>
							<div className="abf-summary-count">
								{summaryCount("pesertaFemale")} orang
							</div>
							<div className="abf-summary-donation">
								Rp {summaryDonation("pesertaFemale").toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button
									className="abf-summary-icon-btn abf-summary-edit-btn"
									onClick={() => editSummaryCount("pesertaFemale", "peserta wanita")}
								>
									<img src="/icons/editnumber.svg" alt="edit jumlah peserta wanita" className="abf-summary-icon" />
								</button>
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "坤道班員助班費", amount: summaryDonation("pesertaFemale")})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

          <div className="abf-summary-row abf-summary-total">
						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Total Peserta
							</div>
							<div className="abf-summary-count">
								{totalPesertaCount()} orang
							</div>
							<div className="abf-summary-donation">
								Rp {totalPesertaDonation().toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "班員助班費", amount: totalPesertaDonation()})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

          {/* PETUGAS */}

          <div
            className="abf-summary-group-title"
            style={{ marginTop: "1rem" }}
          >
            {isZh() ? "辦事" : "Petugas"}
          </div>

          <div className="abf-summary-row">
						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Petugas Pria
							</div>
							<div className="abf-summary-count">
								{summaryCount("petugasMale")} orang
							</div>
							<div className="abf-summary-donation">
								Rp {summaryDonation("petugasMale").toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button
									className="abf-summary-icon-btn abf-summary-edit-btn"
									onClick={() => editSummaryCount("petugasMale", "petugas pria")}
								>
									<img src="/icons/editnumber.svg" alt="edit jumlah petugas pria" className="abf-summary-icon" />
								</button>
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "乾道辦事助班費", amount: summaryDonation("petugasMale")})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

          <div className="abf-summary-row">
						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Petugas Wanita
							</div>
							<div className="abf-summary-count">
								{summaryCount("petugasFemale")} orang
							</div>
							<div className="abf-summary-donation">
								Rp {summaryDonation("petugasFemale").toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button
									className="abf-summary-icon-btn abf-summary-edit-btn"
									onClick={() => editSummaryCount("petugasFemale", "petugas wanita")}
								>
									<img src="/icons/editnumber.svg" alt="edit jumlah petugas wanita" className="abf-summary-icon" />
								</button>
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "坤道辦事助班費", amount: summaryDonation("petugasFemale")})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

          <div className="abf-summary-row abf-summary-total">

						<div className="abf-summary-row-left">
							<div className="abf-summary-label">
								Total Petugas
							</div>
							<div className="abf-summary-count">
								{totalPetugasCount()} orang
							</div>
							<div className="abf-summary-donation">
								Rp {totalPetugasDonation().toLocaleString()}
							</div>
						</div>
						{canWrite && (
							<div className="abf-summary-actions">
								<button 
									className="abf-summary-icon-btn abf-summary-insert-btn"
									onClick={() => insertDonationTransaction({transactionName: "辦事助班費", amount: totalPetugasDonation()})}
								>
									<img src="/icons/inserttrx.svg" alt="insert sebagai donasi" className="abf-summary-icon" />
								</button>
							</div>
						)}
          </div>

        </div>

        {/* ================= TABLES ================= */}

        {renderFinanceTable({
          title: isZh() ? "收入" : "Donasi",
          type: "incomes",
          amountLabel: isZh() ? "金額" : "Jumlah Diterima",
          showOriginal: false
        })}

        {renderFinanceTable({
          title: isZh() ? "支出" : "Biaya & Pengeluaran",
          type: "expenses",
          amountLabel: isZh() ? "金額" : "Jumlah Dibayarkan",
          showOriginal: true
        })}

        {/* ================= BALANCE ================= */}

        <div
          style={{
            marginTop: "2rem",
            padding: "1rem",
            background: "#f8fafc",
            borderRadius: "0.75rem",
            textAlign: "right",
            fontSize: "1.2rem",
            fontWeight: 700
          }}
        >
          {isZh() ? "總計" : "Total"}:
          {" "}
          Rp {balance.toLocaleString()}
        </div>

        {/* ================= INPUT FORM ================= */}

        {canWrite && (
          isAdding || editingIndex !== null
        ) && (

          <div className="rundown-card">

            <div className="r-inline">

              <div className="form-row">

                <label>
                  {isZh() ? "日期 *" : "Tanggal *"}
                </label>

                <input
                  type="date"
                  value={draft.TransactionDate}
                  onChange={(e) =>
                    setDraft(prev => ({
                      ...prev,
                      TransactionDate: e.target.value
                    }))
                  }
                />

              </div>

              <div className="form-row">

                <label>
                  {isZh() ? "類型 *" : "Tipe *"}
                </label>

                <select
                  value={draft.transactionType}
                  disabled={editingIndex !== null}
                  onChange={(e) =>
                    setDraft(prev => ({
                      ...prev,
                      transactionType: e.target.value,
                      originalAmount: "",
                      fullDonation: false
                    }))
                  }
                >
                  <option value="Income">
                    {isZh() ? "收入" : "Donasi"}
                  </option>

                  <option value="Expense">
                    {isZh()
                      ? "支出"
                      : "Biaya & Pengeluaran"}
                  </option>

                </select>

              </div>

            </div>

            <div className="form-row">

              <label>
                {isZh()
                  ? "項目名稱 *"
                  : "Nama Transaksi *"}
              </label>

              <input
                type="text"
                value={draft.transactionName}
                onChange={(e) =>
                  setDraft(prev => ({
                    ...prev,
                    transactionName: e.target.value
                  }))
                }
              />

            </div>

            <div className="form-row">

              <label>
                {isZh()
                  ? "實際收入 / 支出 *"
                  : "Jumlah Tercatat *"}
              </label>

              <input
                type="number"
                value={draft.recordedAmount}
                disabled={draft.fullDonation === true}
                onChange={(e) =>
                  setDraft(prev => ({
                    ...prev,
                    recordedAmount: e.target.value
                  }))
                }
              />

            </div>

            {draft.transactionType === "Expense" && (
              <div className="form-row">

                <label>
                  {isZh()
                    ? "原始金額"
                    : "Jumlah Asli"}
                </label>

                <input
                  type="number"
                  value={draft.originalAmount}
                  onChange={(e) =>
                    setDraft(prev => ({
                      ...prev,
                      originalAmount: e.target.value
                    }))
                  }
                />

              </div>
            )}

            {draft.transactionType === "Expense" && (
              <div className="r-notes-toggle">

                <input
                  type="checkbox"
                  checked={draft.fullDonation}
                  onChange={(e) => {

                    const checked = e.target.checked;

                    setDraft(prev => ({
                      ...prev,
                      fullDonation: checked,
                      recordedAmount:
                        checked
                          ? 0
                          : prev.recordedAmount
                    }));
                  }}
                />

                <label>
                  {isZh()
                    ? "了愿"
                    : "Donasi Penuh"}
                </label>

              </div>
            )}

            <div className="form-row">

              <label>
                {isZh()
                  ? "備註"
                  : "Catatan / Detil"}
              </label>

              <textarea
                value={draft.Remarks || ""}
                onChange={(e) =>
                  setDraft(prev => ({
                    ...prev,
                    Remarks: e.target.value
                  }))
                }
              />

            </div>

            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "0.5rem"
              }}
            >

              <button
                type="button"
                className="btn btn-secondary"
                onClick={handleCancel}
              >
                {isZh() ? "取消" : "Batal"}
              </button>

              <button
                type="button"
                className="btn btn-primary"
                onClick={handleSaveRow}
              >
                {isZh() ? "保存" : "Simpan"}
              </button>

            </div>

          </div>

        )}

        {canWrite &&
          !isAdding &&
          editingIndex === null && (

          <div className="r-add-wrapper">

            <button
              className="btn btn-ghost btn-full-center"
              onClick={handleAdd}
            >
              {isZh()
                ? "+ 新增交易備註"
                : "+ Tambah Catatan Transaksi"}
            </button>

          </div>

        )}

        {canWrite && (
          <div
            style={{
              marginTop: "1rem",
              textAlign: "right"
            }}
          >

            <button
              className="btn btn-primary"
							disabled={isSubmitting}
              onClick={handleSubmit}
            >
              {isSubmitting
								? (isZh() ? "提交中..." : "Loading...")
								: (isZh() ? "提交" : "Submit")}
            </button>

          </div>
        )}

      </div>

      <style>{`
        .meta-pill{
          background:#e6eef8;
          color:#244f8f;
          padding:0.15rem 0.5rem;
          border-radius:999px;
          font-size:1rem;
          margin-right:0.4rem;
        }

        .r-table{
          width:100%;
          border-collapse:collapse;
        }

        .r-table th{
          text-align:left;
          padding:0.5rem;
          border-bottom:1px solid #e6eef8;
          font-size:1rem;
        }

        .r-table td{
          padding:0.6rem 0.5rem;
          vertical-align:top;
          border-bottom:1px solid #f1f5f9;
          font-size:1rem;
        }

        .r-th-drag{
          width:28px;
        }

        .r-td-drag{
          text-align:center;
        }

        .drag-handle{
          cursor:grab;
          font-weight:bold;
          color:#94a3b8;
          user-select:none;
        }

        .drag-handle:active{
          cursor:grabbing;
        }

        .r-th-action{
          width:5rem;
        }

        .r-action-group{
          display:flex;
          gap:0.4rem;
          align-items:center;
        }

        .btn-primary{
          background:#2563eb;
          color:#fff;
        }

        .btn-secondary{
          background:#475569;
          color:#fff;
        }

        .btn-edit{
          background:#f1f5f9;
          color:#fff;
          opacity:0.85;
        }

        .btn-edit:hover{
          background:#eaecf0;
        }

        .btn-danger{
          background:#dc2626c6;
          color:#fff;
          opacity:0.9;
        }

        .btn-danger:hover{
          background:#dc2626f6;
        }

        .btn-ghost{
          background:#f1f5f9;
          color:#334155;
          border:1px solid #e2e8f0;
        }

        .btn-ghost:hover{
          background:#e2e8f0;
        }

        .btn-icon{
          width:30px;
          height:30px;
          display:flex;
          align-items:center;
          justify-content:center;
        }

        .btn-full-center{
          width:100%;
          text-align:center;
          justify-content:center;
        }

        .r-add-wrapper{
          margin-top:1rem;
        }

        .rundown-card{
          border:1px solid #e6eef8;
          border-radius:0.75rem;
          padding:1rem;
          margin-top:1rem;
          background:#fff;
        }

        .form-row{
          display:flex;
          flex-direction:column;
          margin-bottom:0.6rem;
          gap:0;
        }

        .form-row label{
          font-size:0.85rem;
          font-weight:600;
          margin-bottom:0.25rem;
        }

        .r-inline{
          display:flex;
          gap:1rem;
        }

        .r-notes-toggle{
          display:flex;
          align-items:center;
          gap:0.5rem;
          margin-bottom:0.75rem;
          font-size:0.85rem;
          font-weight:600;
        }

        .r-notes-toggle input{
          width:20px;
        }

        input,select,textarea{
          font-size:0.9rem;
          padding:0.5rem;
          border-radius:0.5rem;
          border:1px solid #dbe3ef;
          width:98%;
        }

        textarea{
          min-height:80px;
        }

        .participant-summary-header{
          display:flex;
          justify-content:space-between;
          align-items:center;
          gap:1rem;
          margin-bottom:1rem;
        }

        .participant-group-title{
          font-size:1rem;
          font-weight:700;
          color:#1e293b;
          margin-bottom:0.5rem;
        }

        .participant-row{
					display:grid;
					grid-template-columns:
						minmax(10rem,1fr)
						auto
						auto
						auto;
					align-items:center;
					gap:0.75rem;
					padding:0.5rem 0;
					border-bottom:1px solid #e2e8f0;
				}

        .participant-row.total-row{
          font-weight:700;
          background:#f1f5f9;
          border-radius:0.5rem;
          padding:0.75rem;
          margin-top:0.35rem;
        }

				.summary-icon-btn{
					width:2.2rem;
					height:2.2rem;
					border:none;
					border-radius:0.55rem;
					display:flex;
					align-items:center;
					justify-content:center;
					cursor:pointer;
					transition:0.15s;
					padding:0;
				}

				.summary-icon-btn:hover{
					transform:translateY(-1px);
				}

				.summary-insert-btn{
					background:#2563eb;
				}

				.summary-icon-btn:not(.summary-insert-btn){
					background:#f1f5f9;
					border:1px solid #dbe3ef;
				}

				.abf-summary-card{
					border:1px solid #e6eef8;
					border-radius:0.75rem;
					background:#f8fafc;
					padding:1rem;
					margin-bottom:1rem;
				}

				.abf-summary-header{
					display:flex;
					justify-content:space-between;
					align-items:center;
					gap:1rem;
					margin-bottom:1rem;
				}

				.abf-summary-group-title{
					font-size:1rem;
					font-weight:700;
					color:#1e293b;
					margin-bottom:0.5rem;
				}

				.abf-summary-row{
					display:flex;
					align-items:center;
					justify-content:space-between;
					gap:1rem;
					padding:0.7rem 0;
					border-bottom:1px solid #e2e8f0;
				}

				.abf-summary-row-left{
					min-width:0;
					flex:1;
					display:flex;
					align-items:center;
					gap:1rem;
				}

				.abf-summary-label{
					font-weight:700;
					min-width:9rem;
					color:#0f172a;
				}

				.abf-summary-count{
					min-width:5rem;
					text-align:right;
					white-space:nowrap;
					color:#1e293b;
				}

				.abf-summary-donation{
					min-width:8.5rem;
					text-align:right;
					white-space:nowrap;
					color:#1e293b;
				}

				.abf-summary-actions{
					display:flex;
					align-items:center;
					gap:0.45rem;
					flex-shrink:0;
				}

				.abf-summary-total{
					background:#f1f5f9;
					border-radius:0.6rem;
					padding:0.8rem;
					margin-top:0.35rem;
					font-weight:700;
				}

				.abf-summary-icon-btn{
					width:2.2rem;
					height:2.2rem;
					border:none;
					border-radius:0.55rem;
					display:flex;
					align-items:center;
					justify-content:center;
					cursor:pointer;
					transition:0.15s;
					padding:0;
					flex-shrink:0;
				}

				.abf-summary-icon-btn:hover{
					transform:translateY(-1px);
				}

				.abf-summary-insert-btn{
					background:#2563eb;
				}

				.abf-summary-edit-btn{
					background:#f8fafc;
					border:1px solid #dbe3ef;
				}

				.abf-summary-icon{
					width:1.8rem;
					height:1.8rem;
					object-fit:contain;
				}		
      `}</style>
    </>
  );
}